import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { ReactComponent as IconMenu } from'../assets/icons/6cfb049a-55b0-4dbe-a45b-f577ad60333f.svg'
import useCurrentUser from '../hooks/useCurrentUser';

const SidebarContainer = styled.div`
  width: ${props => (props.isVisible ? '250px' : '0')};
  height: 100vh;
  background: #007bff;
  padding: ${props => (props.isVisible ? '70px 10px 10px 10px' : '0')};
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow-x: hidden;
  transition: width 0.3s, padding 0.3s;
`;

const SidebarLink = styled(NavLink)`
  background: none;
  border: none;
  text-align: left;
  font: inherit;
  cursor: pointer;
  color: white;
  text-decoration: none;
  margin: 3px 0;
  font-size: 15px;
  width: 100%;
  padding: 5px;
  border-radius: 5px;
  transition: background 0.3s;

  &.active {
    background: #0056b3;
  }

  &:hover {
    background: #0056b3;
  }
`;

const ToggleButton = styled.button`
  position: fixed;
  top: 10px;
  left: ${props => (props.isVisible ? '20px' : '20px')};
  height: 37px;
  background: #007bff;
  color: white;
  border: none;
  padding: 3px;
  cursor: pointer;
  transition: left 0.3s;
  z-index: 1000;

  &:hover {
    background: #0056b3;
  }

  svg {
    width: 30px;
    height: 30px;
  }
`;

const GroupLabel = styled.div`
  color: rgba(255, 255, 255, 0.65);
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 5px;
  margin-top: 14px;
  &:first-of-type {
    margin-top: 4px;
  }
`;

const UserFooter = styled.div`
  margin-top: auto;
  padding: 10px 5px;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  width: 100%;
  color: white;
  font-size: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserName = styled.div`
  font-weight: bold;
  font-size: 13px;
`;

const UserMeta = styled.div`
  opacity: 0.8;
`;

/* Collapse arrow: pinned to the sidebar's right edge, same row/height as the menu icon */
const CollapseArrow = styled.button`
  position: fixed;
  top: 10px;
  left: 205px;
  width: 37px;
  height: 37px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: ${(props) => (props.isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  &:hover {
    background: #0056b3;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const STORAGE_KEY = 'binviewer.sidebarVisible';

const Sidebar = ({ setIsSidebarVisible, signOut, user }) => {
  // remembered across reloads so a collapsed nav stays collapsed
  const [isVisible, setIsVisible] = useState(() => localStorage.getItem(STORAGE_KEY) !== 'false');
  const { user: me, isAdmin } = useCurrentUser();
  const canViewCustomers = !!me?.canViewCustomers;
  useEffect(() => {
    setIsSidebarVisible(isVisible);
    localStorage.setItem(STORAGE_KEY, String(isVisible));
  }, [isVisible, setIsSidebarVisible]);
  const toggleSidebar = () => setIsVisible((v) => !v);

  return (
    <>
      <ToggleButton onClick={toggleSidebar} isVisible={isVisible}>
       <IconMenu width="30px" height="30px" />
      </ToggleButton>
      <CollapseArrow onClick={toggleSidebar} isVisible={isVisible} title="Collapse navigation" aria-label="Collapse navigation">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </CollapseArrow>
      <SidebarContainer isVisible={isVisible}>
        <GroupLabel>Monitoring</GroupLabel>
        <SidebarLink to="/insights">Insights</SidebarLink>
        <SidebarLink to="/containers">Containers</SidebarLink>
        {(isAdmin || canViewCustomers) && (
          <>
            <GroupLabel>Manage</GroupLabel>
            {isAdmin && <SidebarLink to="/accounts">Accounts</SidebarLink>}
            {canViewCustomers && <SidebarLink to="/customers">Customers</SidebarLink>}
            {isAdmin && <SidebarLink to="/users">Users</SidebarLink>}
          </>
        )}

        <UserFooter title={me?.email}>
          <UserName>{me?.username || user?.username}</UserName>
          {me?.email && <UserMeta>{me.email}</UserMeta>}
          {isAdmin && <UserMeta>admin</UserMeta>}
        </UserFooter>
        <SidebarLink as="button" onClick={()=>{
          signOut()
        }}>Logout</SidebarLink>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
